import React, { useEffect, useMemo } from 'react';
import { matchPath, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { useEventLogger } from '../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';
import { usePageTimeTracking } from '../../lib/pageTimeTracking/usePageTimeTracking';
import { useInitialState } from '../../lib/context/InitialStateContext';
import { getRumClient } from '../../lib/rum/rumClient';
import { cleanupStaleCache } from './confirmation/previewCache';
import { getAutoApplyBuckets } from './autoApplyDiagnostics';
import { NavigateToExternalUrl } from './components/NavigateToExternalUrl';
import { APPLY_FOR_ME_PROMO_PATH } from './success/ApplyForMePromoPage';

/**
 * Root component for all /auto-apply/* routes.
 * Gates access via the `auto_apply_main_tog` proctor, cleans up stale
 * preview cache, and logs page view / stayed events.
 */
const AutoApplyRoot: React.FC = () => {
    const { pathname } = useLocation();
    const { draftId } = useParams<{ draftId?: string }>();
    const eventLogger = useEventLogger();
    const { getGroup } = useProctorGroups();
    const { homepageUrl, returnUrl } = useInitialState();

    const isAutoApplyEnabled = getGroup('auto_apply_main_tog')?.value === 1;
    const isSubmitForMeDisabled = getGroup('apply_for_me_sfm_tog')?.value === 0;
    const shouldRedirectPromo =
        matchPath(APPLY_FOR_ME_PROMO_PATH, pathname) !== null && isSubmitForMeDisabled;
    const [searchParams] = useSearchParams();
    const draftIdsParam = searchParams.get('draftIds');
    const draftIds = useMemo(() => {
        if (!draftIdsParam) return [];
        return draftIdsParam.split(',').filter(Boolean);
    }, [draftIdsParam]);

    usePageTimeTracking(pathname, (stayedPage, timeStayedOnPageMs) => {
        if (!isAutoApplyEnabled || shouldRedirectPromo) return;
        eventLogger.logReviewPageStayed?.({ stayedPage, timeStayedOnPageMs });
    });

    useEffect(() => {
        if (!isAutoApplyEnabled || shouldRedirectPromo) return;
        eventLogger.logReviewPageView?.({
            currentPage: pathname,
            draftId: AvroOptional.string(draftId),
        });
    }, [isAutoApplyEnabled, shouldRedirectPromo, pathname, draftId, eventLogger]);

    useEffect(() => {
        cleanupStaleCache(draftIds);
    }, [draftIds]);

    // RUM-based metrics see all sessions, but only events that carry the buckets can be split.
    const { bannerBucket, dqBucket } = isAutoApplyEnabled
        ? getAutoApplyBuckets(getGroup)
        : { bannerBucket: null, dqBucket: null };
    useEffect(() => {
        try {
            const rum = getRumClient();
            rum?.setGlobalContextProperty?.('bannerBucket', bannerBucket);
            rum?.setGlobalContextProperty?.('dqBucket', dqBucket);
        } catch {
            /* Telemetry must never alter navigation. */
        }
        // In-app navigation can leave /auto-apply without a reload; do not tag other routes.
        return () => {
            try {
                const rum = getRumClient();
                rum?.removeGlobalContextProperty?.('bannerBucket');
                rum?.removeGlobalContextProperty?.('dqBucket');
            } catch {
                /* Telemetry must never alter navigation. */
            }
        };
    }, [bannerBucket, dqBucket]);

    if (!isAutoApplyEnabled) {
        return <NavigateToExternalUrl url={homepageUrl} />;
    }

    if (shouldRedirectPromo) {
        return <NavigateToExternalUrl url={returnUrl} />;
    }

    return <Outlet />;
};

export default AutoApplyRoot;
